import axios from 'axios'

const API_BASE = '/api'

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  xsrfCookieName: 'csrftoken',
  xsrfHeaderName: 'X-CSRFToken',
})

// Ensure we have a CSRF cookie before any unsafe request
let csrfReady: Promise<void> | null = null
export function ensureCsrf(): Promise<void> {
  if (csrfReady) return csrfReady
  csrfReady = api.get('/auth/csrf/').then(() => undefined)
  return csrfReady
}

// ─── 401 handler ──────────────────────────────────────────────────────
// AuthProvider wires `onUnauth()` here at mount. When ANY API response
// comes back 401, we call it so React state flips to unauthenticated
// and the Login screen renders. Catches:
//   - mid-session role revocation (admin disabled the account)
//   - cross-tab logout (other tab cleared the cookie)
//   - server-side session expiry
//
// Excluded paths: the auth endpoints themselves. /auth/me/ returns 401
// when not logged in (that's its job during initial load — we'd loop
// forever otherwise). /auth/login/ returns 401 on bad creds (the user
// is trying to log in, don't kick them out). /auth/logout/ explicitly
// 401s if there's no session — also expected. /auth/csrf/ is anonymous.
const UNAUTH_EXEMPT_PATHS = [
  '/auth/me/',
  '/auth/login/',
  '/auth/logout/',
  '/auth/csrf/',
]
let onUnauthHandler: (() => void) | null = null
export function setUnauthHandler(fn: (() => void) | null) {
  onUnauthHandler = fn
}
api.interceptors.response.use(
  (resp) => resp,
  (err) => {
    const status = err?.response?.status
    const url: string = err?.config?.url || ''
    if (status === 401 && !UNAUTH_EXEMPT_PATHS.some((p) => url.endsWith(p))) {
      if (onUnauthHandler) onUnauthHandler()
    }
    return Promise.reject(err)
  },
)

// Citation forms (content) + how they're drawn (style). KEN only in the UI.
export type CitationContent = 'code' | 'first' | 'first_subject'
export type CitationStyle = 'underline' | 'chip'

export type User = {
  id: number
  username: string
  email: string
  is_staff: boolean
  is_superuser: boolean
  role: string
  client: { id: number; code: string; name: string } | null
  /** Backend-computed: true for superusers and role='studio_editor' /
   *  'studio_viewer'. Single check the UI uses to decide whether to
   *  render the Studio nav. */
  has_studio_access: boolean
  /** Set when a superuser is acting-as another user. Null otherwise. */
  acting_as?: {
    real_user_id: number
    real_username: string
    real_is_superuser: boolean
  } | null
  /** Deployment flag: when true, studio_editor users may create/edit/
   *  promote PromptSlots + PromptVersions on production slots (not just
   *  sandbox). On for dev-2 (pipeline-v2 lab); off everywhere else. */
  studio_editor_full_prompt_access?: boolean
  /** Backend-computed: true for the client-owner tier (attorney / admin) and
   *  superusers. Gates the owner Walls page + pane-3 affordance. */
  can_manage_walls?: boolean
  /** Non-owner tenant user (Maria) who may banish senders from their OWN
   *  received mail. Gets the limited one-form Walls page. */
  can_wall_own_mail?: boolean
  /** Per-user opt-in (shadow_ui flag) for the "Proposed Agreements" pane-1
   *  gutter. OFF by default; flipped on for Heather/Maria. Reflects the
   *  effective (impersonated) user. (Ken 2026-10-13) */
  cic_agreements_gutter?: boolean
  /** Per-user opt-in (shadow_ui flag) for the Quiet nav control. Hidden by
   *  default; flipped on for a user during testing. (Ken 2026-10-19) */
  quiet_enabled?: boolean
  /** Per-user reading size for panes 2 & 3 (accessibility). Default 'normal'.
   *  Lives on the profile so it follows the user across machines. (Maria 2026-10-19) */
  reading_size?: 'normal' | 'large' | 'larger'
  /** Per-user chosen Quiet chime — an id into the firm's ChimeSound library, or
   *  null = the built-in default chime. (Ken 2026-10-19) */
  chime_id?: number | null
  /** Citation style (per-user; KEN). `citation_content` = which form the in-chat
   *  citations take; `citation_style` = how they're drawn. (Ken 2026-10-24) */
  citation_content?: CitationContent
  citation_style?: CitationStyle
}

/** An ethical wall — the persistent rule that hides certain email content
 *  from non-owner users. Kind 'thread' walls an email + its whole thread;
 *  kind 'sender' walls everything from an address. */
export type EthicalWall = {
  id: number
  kind: 'thread' | 'sender' | 'sender_subject'
  /** 'hidden' = banished (invisible to everyone incl. creator); 'restricted' =
   *  hidden from other users, creator keeps access. */
  hide_mode: 'hidden' | 'restricted'
  sender_email: string
  subject_contains: string
  /** Non-null when the wall only covers mail addressed to this user (Maria's
   *  own-mail banish). Null for owner walls (any recipient). */
  recipient_email: string | null
  reason: string
  created_by: string | null
  created_by_id: number | null
  created_at: string
  covered_count: number
  label: string
  sample_from: string
}

export async function listWalls(): Promise<EthicalWall[]> {
  const r = await api.get('/walls/')
  return r.data
}

/** Create + materialise a wall. `match` sender|sender_subject; `mode`
 *  hidden(banish)|restricted. Pass `senderEmail` or `artifactId` (the backend
 *  resolves the sender from a clicked email). Non-owner users are forced to a
 *  sender/hidden/own-mail wall server-side regardless of what's passed. */
export async function createWall(p: {
  match?: 'sender' | 'sender_subject'
  mode?: 'hidden' | 'restricted'
  senderEmail?: string
  artifactId?: number
  subject?: string
  reason?: string
}): Promise<EthicalWall & { walled_count: number }> {
  await ensureCsrf()
  const r = await api.post('/walls/', {
    match: p.match, mode: p.mode, sender_email: p.senderEmail,
    artifact_id: p.artifactId, subject: p.subject, reason: p.reason,
  })
  return r.data
}

export async function deleteWall(id: number): Promise<{ unwalled: boolean; rederived_count: number }> {
  await ensureCsrf()
  const r = await api.delete(`/walls/${id}/`)
  return r.data
}

/* ─── Quiet (per-user trusted alerts) ────────────────────────────────── */

/** A per-user Quiet rule: watch a PERSON (by email) or a MATTER. `label` is the
 *  abbreviated chip shown in the nav popup ("Sandra L.", "Blockson"). */
export type QuietRule = {
  id: number
  kind: 'person' | 'matter'
  person_email: string
  person_name: string   // full roster name (blank for an off-roster address) — tooltip only
  matter_id: number | null
  matter_name: string   // full matter name — tooltip only
  label: string
  active: boolean
  created_at: string
}

export type QuietResolveResult =
  | { status: 'created'; rule: QuietRule }
  | { status: 'ask'; prompt: string; options: { label: string; value: string | number; sub?: string }[] }

export async function listQuietRules(): Promise<{ active: QuietRule[]; inactive: QuietRule[] }> {
  const r = await api.get('/quiet-rules/')
  return r.data
}

/** The one-line rule-builder. `kind` = which button was pressed. Pass `text` (a
 *  phrase) to resolve, or `chosenEmail` / `chosenMatterId` to confirm a picked
 *  option. Returns the created rule, or an ask with pickable options. */
export async function resolveQuietRule(p: {
  kind: 'person' | 'matter'
  text?: string
  chosenEmail?: string
  chosenMatterId?: number
}): Promise<QuietResolveResult> {
  await ensureCsrf()
  const r = await api.post('/quiet-rules/resolve/', {
    kind: p.kind, text: p.text,
    chosen_email: p.chosenEmail, chosen_matter_id: p.chosenMatterId,
  })
  return r.data
}

export async function toggleQuietRule(id: number): Promise<QuietRule> {
  await ensureCsrf()
  const r = await api.post(`/quiet-rules/${id}/toggle/`)
  return r.data
}

export async function deleteQuietRule(id: number): Promise<void> {
  await ensureCsrf()
  await api.delete(`/quiet-rules/${id}/`)
}

/** An unacknowledged Quiet hit — a watched person/matter just had new inbound
 *  email. Drives the nav pip; `matter_id` + `email_artifact_id` build the drill-in. */
export type QuietHit = {
  id: number
  rule_label: string
  matter_id: number | null
  email_artifact_id: number | null
  email_issue_id: number | null
  sender: string
  created_at: string
}

export async function listQuietHits(): Promise<{ unseen_count: number; hits: QuietHit[] }> {
  const r = await api.get('/quiet-hits/')
  return r.data
}

export async function markQuietHitSeen(id: number): Promise<QuietHit> {
  await ensureCsrf()
  const r = await api.post(`/quiet-hits/${id}/seen/`)
  return r.data
}

export async function markAllQuietHitsSeen(): Promise<{ marked: number }> {
  await ensureCsrf()
  const r = await api.post('/quiet-hits/seen-all/')
  return r.data
}

/* ─── Quiet chime sounds (per-firm library; QA upload, View & Sounds pick) ─── */

export type ChimeSound = { id: number; name: string; filename: string; byte_size: number }

/** Same-origin URL for one chime's audio — feed straight to `new Audio(url)`. */
export function chimeSoundAudioUrl(id: number): string {
  return `${API_BASE}/quiet/chime-sounds/${id}/audio/`
}

export async function listChimeSounds(): Promise<ChimeSound[]> {
  const r = await api.get('/quiet/chime-sounds/')
  return r.data.sounds ?? []
}

/** QA (studio): upload a .zip of audio files → REPLACES the firm's chime library. */
export async function uploadChimeSoundsZip(file: File): Promise<{ sounds: ChimeSound[]; skipped: string[] }> {
  await ensureCsrf()
  const fd = new FormData()
  fd.append('zip', file)
  const r = await api.post('/quiet/chime-sounds/upload/', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return r.data
}

/** QA (studio): set the display name shown for a chime in Settings. */
export async function renameChimeSound(id: number, name: string): Promise<ChimeSound> {
  await ensureCsrf()
  const r = await api.patch(`/quiet/chime-sounds/${id}/`, { name })
  return r.data
}

export async function deleteChimeSound(id: number): Promise<void> {
  await ensureCsrf()
  await api.delete(`/quiet/chime-sounds/${id}/`)
}

/* ─── Patent docs (studio-only download area in QA) ──────────────────── */

export type PatentDoc = { key: string; filename: string; bytes: number }

export async function listPatentDocs(): Promise<PatentDoc[]> {
  const r = await api.get<Record<string, { filename: string; bytes: number }>>(
    '/qa/patent-doc/', { params: { list: 1 } })
  return Object.entries(r.data).map(([key, v]) => ({ key, filename: v.filename, bytes: v.bytes }))
}

/** Same-origin download URL (session cookie authenticates the studio-gated view). */
export function patentDocDownloadUrl(key: string): string {
  return `${API_BASE}/qa/patent-doc/?name=${encodeURIComponent(key)}`
}

/* ─── Delete (matter-level, Privacy → Delete) ────────────────────────── */

/** Level a — remove a matter (and its solely-owned emails/docs) from view;
 *  admin-recoverable. Shared artifacts only unlink. */
export async function removeMatter(matterId: number):
    Promise<{ matter_seq: number; removed: number; unlinked_shared: number }> {
  await ensureCsrf()
  const r = await api.post(`/matters/${matterId}/remove/`, {})
  return r.data
}

/** Level b — permanently purge a matter's solely-owned content (scrub +
 *  tombstone + certificate). Irreversible. */
export async function purgeMatter(matterId: number, reason?: string):
    Promise<{ certificate_id: number; matter_seq: number; purged: number; unlinked_shared: number }> {
  await ensureCsrf()
  const r = await api.post(`/matters/${matterId}/purge/`, { confirm: 'DELETE', reason })
  return r.data
}

export type DeletionCertificate = {
  id: number
  matter_seq: number
  matter_name: string
  item_count: number
  reason: string
  purged_by: string | null
  purged_at: string
}

export async function listDeletionCertificates(): Promise<DeletionCertificate[]> {
  const r = await api.get('/deletion-certificates/')
  return r.data
}

/** Direct download URL for a certificate's attestation (.txt). */
export function deletionCertificateUrl(id: number): string {
  return `/api/deletion-certificates/${id}/attestation/`
}

// ─── Control-bar banner (QA) ──────────────────────────────────────────
export type NavBanner = {
  id: number
  data_url: string
  filename: string
  content_type: string
  width: number | null
  height: number | null
  byte_size: number
  is_active: boolean
  created_at: string
}
export type NavBannerPayload = { active: NavBanner | null; banners: NavBanner[]; was_active?: boolean }

export async function listNavBanners(): Promise<NavBannerPayload> {
  const r = await api.get<NavBannerPayload>('/qa/nav-banner/')
  return r.data
}
export async function uploadNavBanner(body: {
  data_url: string; filename?: string; content_type?: string
  width?: number | null; height?: number | null; byte_size?: number
}): Promise<NavBannerPayload> {
  await ensureCsrf()
  const r = await api.post<NavBannerPayload>('/qa/nav-banner/', body)
  return r.data
}
export async function activateNavBanner(id: number): Promise<NavBannerPayload> {
  await ensureCsrf()
  const r = await api.post<NavBannerPayload>(`/qa/nav-banner/${id}/activate/`, {})
  return r.data
}
export async function clearNavBanner(): Promise<NavBannerPayload> {
  await ensureCsrf()
  const r = await api.post<NavBannerPayload>('/qa/nav-banner/clear/', {})
  return r.data
}
export async function deleteNavBanner(id: number): Promise<NavBannerPayload> {
  await ensureCsrf()
  const r = await api.post<NavBannerPayload>(`/qa/nav-banner/${id}/delete/`, {})
  return r.data
}

// ─── Main-B: per-user private experimental homepage config ────────────
export type ShadowSlot = { type: 'image'; data: string; filename?: string }
export type ReadingSize = 'normal' | 'large' | 'larger'

export type ShadowUi = {
  enabled?: boolean
  nav_color?: string
  nav_banner?: string          // data URL
  font?: string                // FontKey (see productName.ts FONTS)
  slots?: Record<string, ShadowSlot>
  reading_size?: ReadingSize   // per-user reading size for panes 2 & 3
  chime_id?: number | null     // per-user chosen Quiet chime (null = default)
  citation_content?: CitationContent  // in-chat citation form (KEN)
  citation_style?: CitationStyle      // in-chat citation look (KEN)
}
export async function getShadowUi(): Promise<ShadowUi> {
  const r = await api.get<{ shadow_ui: ShadowUi }>('/shadow-ui/')
  return r.data.shadow_ui || {}
}
export async function saveShadowUi(cfg: ShadowUi): Promise<ShadowUi> {
  await ensureCsrf()
  const r = await api.put<{ shadow_ui: ShadowUi }>('/shadow-ui/', { shadow_ui: cfg })
  return r.data.shadow_ui || {}
}

/** Remove a DOCUMENT artifact (pane-3 toolbar). Docs only; soft-delete,
 *  recoverable; the original stays in Drive. */
export async function removeDocument(artifactId: number):
    Promise<{ ok: boolean; removed_artifact_id: number }> {
  await ensureCsrf()
  const r = await api.post('/remove-document/', { artifact_id: artifactId })
  return r.data
}

/* ─── Cadence (matter status color) ──────────────────────────────────── */

/** A time border → color. Matters fall in the FIRST band whose max_days they
 *  don't exceed; the last band (max_days null) catches everything older. */
export type CadenceBand = { max_days: number | null; color: string }

export async function getMatterCadence(matterId: number):
    Promise<{ days_since: number | null; last_at: string | null }> {
  const r = await api.get(`/matters/${matterId}/cadence/`)
  return r.data
}

export async function getCadenceBands(): Promise<CadenceBand[]> {
  const r = await api.get('/matters/cadence-bands/')
  return r.data
}

export async function setCadenceBands(bands: CadenceBand[]): Promise<CadenceBand[]> {
  await ensureCsrf()
  const r = await api.put('/matters/cadence-bands/', bands)
  return r.data
}

export type ImpersonationTarget = {
  user_id: number
  username: string
  email: string
  role: string
  client_id: number
  client_code: string
  client_name: string
}

export async function listImpersonationTargets(): Promise<ImpersonationTarget[]> {
  const r = await api.get('/auth/impersonation-targets/')
  return r.data.targets
}

export async function setImpersonation(userId: number | null): Promise<{
  acting_as: User['acting_as']
  effective_user_id: number
  effective_username?: string
  effective_client_code?: string | null
}> {
  await ensureCsrf()
  const r = await api.post('/auth/impersonate/', { user_id: userId })
  return r.data
}

export type ClientSettings = {
  id: number
  code: string
  name: string
  /** Prompt-facing client identity — exposed as {{client_name}} (name + common
   *  aliases). Edited in Studio → AI Config. Falls back to `name` when blank. */
  client_name: string
  default_storage_mode: 'cached' | 'on_demand'
  llm_log_retention: 'full' | 'redacted' | 'transient' | 'metadata_only'
  source_storage_overrides: Record<string, string>
  ai_auto_folder_enabled: boolean
  ai_domain_context: string
  // Free-form firm context JSON — exposed as {{client_context}} to every
  // prompt step. Read-only here; edited via /settings.
  context: Record<string, unknown>
  /** Firm lexicon — exposed as {{firm_lexicon}}. Independent of `context`.
   *  Values: a string (single expansion), an array (multiple), or an OBJECT
   *  ({resolves_to, signal?, role?, matter_hint?}) for a recurring named
   *  entity that carries a matching signal. Backend strips empties on save. */
  firm_lexicon?: Record<string, string | string[] | Record<string, unknown>>
  /** Ken 2026-07-19 — Fan-Out entity-gate config. Any list-valued key
   *  contributes to the generic pool consumed by the deterministic
   *  L4→L5 gate in apps.pipeline.anti_echo. Starter shape:
   *   {"roles": [...], "doc_types": [...], "custom": [...]}.
   *  Edited via Studio → AI Config. Empty/absent = gate has no
   *  generic list, so it only trips when entities_found_in_matter
   *  is empty. */
  generic_terms?: Record<string, string[]>
}

export async function login(username: string, password: string): Promise<User> {
  await ensureCsrf()
  const r = await api.post('/auth/login/', { username, password })
  return r.data
}

export async function logout(): Promise<void> {
  await ensureCsrf()
  await api.post('/auth/logout/')
}

export async function me(): Promise<User | null> {
  try {
    const r = await api.get('/auth/me/')
    return r.data
  } catch {
    return null
  }
}

// ---------- Domain types ----------

export type Matter = {
  id: number
  client: number
  client_seq: number
  /** Human-readable matter code, e.g. "M-007". */
  composite_id?: string
  name: string
  matter_type: string
  /** Ken 2026-07-20 — closed-enum classification copied from the
   *  founding EmailIssue's kind. Displayed alongside matter_type in
   *  sidebar + pane-2 chip per Ken 2026-07-21. May be empty when
   *  the matter predates the upstream L3 change or was pre-seeded. */
  kind?: string
  status: string
  /** CIC demo (Ken 2026-10-01): a synthetic, studio-only "proposed" re-do of a
   *  live historical matter. Hidden from non-studio users by the API. */
  demo?: boolean
  /** The live matter this demo matter re-does (demo matters only). */
  source_matter?: number | null
  stage: string
  urgency: number
  summary: string
  /** Brisk inside-baseball list of key events (oldest→newest from AI).
   *  UI displays newest first by reversing. Added per Ken 2026-05-25. */
  bullet_summary?: string[]
  /** Optional structured table. No UI consumer yet (2026-05-25). */
  tabular_summary?: { columns?: string[]; rows?: string[][] } | Record<string, unknown>
  /** AI-narrated diff of what changed. Often null. */
  conceptual_diff?: string
  /** v2 L9 Generate Matter Summary cache (Ken 2026-07-05). Populated by
   *  the pipeline; frontend renders these directly instead of firing an
   *  on-demand LLM call on every matter open. `summary_generated_at` is
   *  null until the first L9 run for this matter. */
  experimental_summary?: string
  summary_generated_at?: string | null
  /** Legacy ranked-keywords list. Kept in sync as a mirror of
   *  unranked_keywords for back-compat. */
  keywords: string[]
  /** Smaller, party-focused keyword set. Treated as a set (no rank). */
  unranked_keywords?: string[]
  /** Full ranked keyword list (max 100). Future UI. */
  ranked_keywords?: string[]
  parties: unknown[]
  waiting_on: string
  /** Themed groupings written back from narrate_matter_history. */
  narrative_groups?: Array<{ label: string; summary: string; items: Array<{ artifact_id: number; note?: string }> }>
  // ── Pipeline v2 fields ──────────────────────────────────────────────
  /** v2 stable matter definition (1-2 sentences) — distinct from
   *  `summary` which becomes on-demand in v2. */
  description?: string
  /** User-rejected folder paths so the AI doesn't re-propose. */
  dismissed_folder_paths?: string[]
  /** Latest output of v2 Categorize Issues by Activity Type. Only set for
   *  litigation/contract matter_types. */
  activity_index?: Array<{
    category_label: string
    items: Array<{ email_issue_id: string; note?: string }>
  }>
  /** Latest output of v2 Group Issues into Phases. Replaces
   *  narrative_groups conceptually; the v2 writeback fills both. */
  phase_groupings?: Array<{
    phase_label: string
    items: Array<{ issue_id: string; note?: string }>
  }>
  /** Pipeline v2: per-matter EmailIssue lookup table so the frontend can
   *  resolve phase_groupings.items[].issue_id and
   *  activity_index.items[].email_issue_id back to the artifact row. */
  email_issues?: Array<{
    issue_id: string
    artifact_id: number
    issue_index: number
  }>
  /** LIST-only presence flags (Ken 2026-09-25). The list serializer omits the
   *  heavy phase_groupings/activity_index/email_issues arrays (they dominate the
   *  payload) and sends these booleans so pane-2 can still show the Phases/Tray
   *  toggles; the full arrays are fetched per-matter (matters.detail) only when
   *  a heavy view opens. Undefined on a full (retrieve) fetch. */
  has_phase_groupings?: boolean
  has_activity_index?: boolean
  /** Active (non-dismissed) folder assignments. Empty array = unfiled. */
  folder_assignments?: Array<{
    id: number
    folder: number
    folder_name: string
    assigned_by: 'user' | 'ai'
    assigned_at: string
  }>
  created_at: string
  updated_at: string
}

export type Folder = {
  id: number
  client: number
  parent_folder: number | null
  name: string
  description: string
  created_by: number | null
  created_by_ai: boolean
  last_edited_at: string | null
  last_edited_by: number | null
  last_edited_by_ai: boolean
  created_at: string
  updated_at: string
  matter_count_self?: number
  matter_count_recursive?: number
  depth: number
}

/** Tree node from /api/folders/tree/ — same shape as Folder plus children. */
export type FolderNode = Folder & {
  children: FolderNode[]
  matter_count_self: number
  matter_count_recursive: number
}

export type MatterFolderAssignment = {
  id: number
  matter: number
  folder: number
  folder_name: string
  matter_name: string
  assigned_by: 'user' | 'ai'
  assigned_by_username: string | null
  assigned_at: string
  dismissed_at: string | null
  dismissed_by_username: string | null
}

// Short identifier: drop the CLIENT-SOURCE prefix (e.g. "MOVU-GOOG-EMAL-038606"
// → "EMAL-038606"). Keeps the type + sequence. This is the canonical human-facing
// artifact code in the WORKSPACE (end-user, single-tenant → the CLIENT-SOURCE
// prefix is redundant). Studio surfaces show the full composite_id instead, so
// the operator always sees the tenant. (Ken 2026-08-17.)
export function shortId(id?: string | null): string {
  if (!id) return ''
  const parts = id.split('-')
  return parts.length >= 4 ? parts.slice(2).join('-') : id
}

// Bare numeric identifier: drop the CLIENT-SOURCE-TYPE parts and the leading
// zeros, leaving just "#38606".
//
// RETIRED from display 2026-08-17: Ken reversed his 2026-08-10 "no EMAL/DOCU/ATCH
// type codes in front of the user" call. The Workspace now shows the type-coded
// shortId() (EMAL-038606) instead. Kept here (currently unreferenced) in case a
// bare-number surface is wanted again — do NOT wire it back into Workspace/Studio
// display without checking with Ken first.
export function numId(id?: string | null): string {
  if (!id) return ''
  const last = id.split('-').pop() || ''
  return /^\d+$/.test(last) ? `#${parseInt(last, 10)}` : `#${last}`
}

export const folders = {
  list: () => api.get<Folder[]>('/folders/').then(r => r.data),
  tree: () => api.get<{ folders: FolderNode[] }>('/folders/tree/').then(r => r.data.folders),
  detail: (id: number) => api.get<Folder>(`/folders/${id}/`).then(r => r.data),
  create: async (payload: { name: string; parent_folder?: number | null; description?: string }) => {
    await ensureCsrf()
    return api.post<Folder>('/folders/', payload).then(r => r.data)
  },
  update: async (id: number, payload: Partial<Pick<Folder, 'name' | 'parent_folder' | 'description'>>) => {
    await ensureCsrf()
    return api.patch<Folder>(`/folders/${id}/`, payload).then(r => r.data)
  },
  remove: async (id: number) => {
    await ensureCsrf()
    return api.delete(`/folders/${id}/`).then(r => r.data)
  },
  assignMatter: async (matter: number, folder: number) => {
    await ensureCsrf()
    return api.post<MatterFolderAssignment>('/matter-folders/', { matter, folder }).then(r => r.data)
  },
  dismissMatter: async (matter: number, folder: number) => {
    await ensureCsrf()
    return api.post<MatterFolderAssignment>('/matter-folders/dismiss/', { matter, folder }).then(r => r.data)
  },
  recentOps: (limit = 25) =>
    api.get<{ ops: Array<{ matter_id: number; matter_name: string; folder_id: number; folder_name: string; op: 'assigned' | 'dismissed'; at: string }> }>(`/matter-folders/recent-ops/?limit=${limit}`)
      .then(r => r.data.ops),
}

/** Accounting "Page" line-item (invoices + non-invoice admin). Superuser-only,
 *  read-only for now. See docs/2026-08-20_accounting-page-design.md. */
export type AccountingItem = {
  id: number
  item_kind: 'invoice' | 'admin'
  direction: 'pay_out' | 'receive_in' | 'internal'
  vendor: string
  invoice_number: string
  po_number: string
  invoice_date: string | null
  amount: string | null
  amount_raw: string
  their_matter_no: string
  re_line: string
  activity: string
  received_at: string | null
  sent_to_ap_at: string | null
  oracle_received: boolean
  oracle_received_at: string | null
  paid: boolean
  paid_at: string | null
  sent_to_ap_email: number | null
  paid_email: number | null
  oracle_email: number | null
  stage: 'received' | 'sent_to_ap' | 'oracle_received' | 'paid'
  source_email: number
  source_email_seq: number | null
  source_email_subject: string
  source_email_composite: string | null
  source_attachment: number | null
  source_attachment_composite: string | null
  linked_matter: number | null
  linked_matter_seq: number | null
  linked_matter_name: string
  related_email_ids: number[]
  notes: string
  created_via: string
  created_at: string
}

export const accounting = {
  list: (params?: { item_kind?: 'invoice' | 'admin' }) =>
    api.get<AccountingItem[] | { results: AccountingItem[] }>('/accounting-items/', { params })
      .then(r => (Array.isArray(r.data) ? r.data : r.data.results)),
  /** Tick / untick a manual stage flag (sent_to_ap | oracle_received | paid). */
  setFlag: async (id: number, field: 'sent_to_ap' | 'oracle_received' | 'paid', value: boolean) => {
    await ensureCsrf()
    return api.post<AccountingItem>(`/accounting-items/${id}/set-flag/`, { field, value }).then(r => r.data)
  },
}

/** One issue raised by an email artifact (pipeline v2 multi-issue display).
 *  label = its matter's name (stem fallback if not yet assigned); excerpt =
 *  first L3 source excerpt, used as the distinguishing clause. */
export type ArtifactIssue = {
  issue_index: number
  label: string
  excerpt: string
  summary: string
  kind: string
  matter_id: number | null
  matter_client_seq: number | null
  filing_decision: string
}

export type ChangeCarryItem = {
  location: string
  characterization: string
  status: 'carried' | 'reverted' | 'modified'
  before: string
  after: string
  source_id: string
}
export type ChangeCarry = {
  carried: number
  total: number
  misses: ChangeCarryItem[]
  changes: ChangeCarryItem[]
  redlined_artifact_id: number
  executed_artifact_id: number
}

export type Artifact = {
  id: number
  client: number
  client_code: string
  client_seq: number
  composite_id: string  // {CLIENT_CODE}-{SOURCE_CODE}-{TYPE_CODE}-{client_seq:06d}
  source_type: 'gmail' | 'gdrive' | 'upload'
  source_external_id: string
  artifact_type: 'email' | 'document' | 'attachment' | 'chat'
  mime_type: string
  subject: string
  from_name: string
  from_email: string
  source_date: string | null
  visibility: 'shared' | 'restricted' | 'hidden'
  parent_artifact: number | null
  matter_ids: number[]
  children_count: { total: number; visible: number }
  // Content-free followup (A′): '' | 'read_receipt' | 'auto_reply' | 'bounce',
  // and the derived pane-2 Tray category (e.g. "Preservation Notices").
  followup_kind?: string
  followup_label?: string
  ignored: boolean
  ignored_reason: string
  // Cross-mailbox dedup. is_duplicate_of points to the primary; null
  // for primaries themselves. duplicate_count is the inverse — how
  // many other mailbox copies are linked to this row (only non-zero
  // for primaries).
  is_duplicate_of: number | null
  duplicate_count: number
  // Promoted AI fields (canonical state, written back from CR after each
  // pipeline step). Useful for list rendering without a detail fetch.
  is_personal: boolean | null
  urgency: number | null
  doc_kind: string
  is_sensitive: boolean | null
  ai_action: string
  ai_summary: string
  /** Pipeline v2: concatenated EmailIssue.summary for issues filed against
   *  this artifact (scoped to the current matter when fetched via
   *  ?matter=N). Empty string if no filed issues exist. Matter timeline
   *  prefers this over ai_summary. */
  email_issue_summary?: string
  /** Ken 2026-07-07 memo — ~4-9-word header labels alongside the two
   *  summary fields. Pane-3 uses these instead of the raw filename /
   *  raw subject when populated. Multi-issue emails join stems with
   *  ' · '. Empty until the L6 summarize step has run. */
  email_issue_stem?: string
  attachment_stem?: string
  /** Pipeline v2 multi-issue display: full per-issue list (not matter-scoped)
   *  + count. issue_count > 1 marks a multi-issue email. */
  issues?: ArtifactIssue[]
  issue_count?: number
  /** Doc-type tags for the pane-2 "Docs" view grouping (KEN demo). An artifact
   *  may carry several and appears under each group. Empty for untagged. */
  doc_types?: string[]
  /** Docs view (KEN T6): version label ("v2 · Northlane redline … · 8/17") and
   *  side ('ridgemont' | 'northlane') for the two-column back-and-forth layout.
   *  ATCH only; null otherwise. */
  doc_version?: string | null
  doc_side?: string | null
  /** Conversation/thread id (KEN T6 threading). Also on ArtifactDetail. */
  gmail_thread_id?: string | null
  /** Pipeline v2: attachment_summary from the Summarize Attachment step
   *  (only meaningful when artifact_type='attachment'). Empty string if
   *  the step hasn't run. */
  attachment_summary?: string
  /** True iff this attachment has at least one extracted redline or
   *  comment in its .docx markup arrays. Drives the green "markup"
   *  badge on attachment cards in pane-2. Always false for non-
   *  attachment artifact types. */
  has_markup?: boolean
  /** Ken 2026-11-02: on an executed attachment, "N of M" redline changes
   *  carried from the redlined-sent version, plus the misses. Null unless
   *  computed (KEN/DEMO only). Drives the pane-2 "changes carried" chip. */
  change_carry?: ChangeCarry | null
  /** Ken 2026-07-03: Google Docs / Sheets / Drive files referenced
   *  by URL in this EMAIL's body. Empty for non-emails. Each entry
   *  is enough to render a pseudo-attachment child row in pane-2
   *  and know what to fetch when the user clicks it. Unresolved
   *  stubs (doc not yet synced from Drive) surface with `doc_id:
   *  null` — the row renders with a "not synced" chip + external
   *  "Open in Docs" link. */
  linked_docs?: LinkedDoc[]
  // email_structural step output (L2). Null for non-emails / not-yet-run.
  email_structural: {
    is_forward: boolean
    copy_in: boolean
    strictly_logistical: boolean
    is_interlineated: boolean
    legal_office_only: boolean
    is_digest: boolean
    multiple_issues_detected: boolean
    issues: string[]
  } | null
  created_at: string
}

export type LinkedDoc = {
  /** EmailDocLink row id. Not an Artifact id. */
  id: number
  /** Referenced Artifact id, null when unresolved. */
  doc_id: number | null
  doc_composite_id: string | null
  doc_subject: string | null
  /** Best display name: resolved doc's subject, else the filename recovered
   *  from the parent email's "shared with you" subject, else null. */
  doc_name: string | null
  /** image | video | doc | sheet | file — drives the card icon + the friendly
   *  fallback label when there's no real name to show. */
  media_kind: 'image' | 'video' | 'doc' | 'sheet' | 'file'
  /** True iff the referenced doc has extracted redlines/comments —
   *  same signal as `has_markup` on regular attachment rows. */
  doc_has_markup: boolean
  doc_mime_type: string | null
  doc_source_external_id: string | null
  /** Drive file_id extracted from the URL. Always present. */
  drive_file_id: string
  doc_kind: 'document' | 'spreadsheet' | 'file'
  /** First-observed URL variant in the email body. */
  source_url: string
  /** True when doc_id is populated. */
  is_resolved: boolean
  /** Per-usage AI summary keyed by EmailIssue.id (as string).
   *  Populated by PHASE 3.6 summarize_attachment fanout. */
  summary_by_issue: Record<string, string>
  /** Concatenated blank-line-separated summaries for the in-scope
   *  issues (`?matter=N` scopes to only that matter's issues; the
   *  full set otherwise). This is what the timeline row renders in
   *  the same slot regular attachments use for `attachment_summary`. */
  attachment_summary: string
}

/** One logged communication (out or in) from the pre-GC intake console. */
export type CommEvent = {
  id: number
  unit_key: string
  direction: string
  recipients: { label: string; email?: string }[]
  summary: string
  is_sandbox: boolean
  created_at: string | null
  by: string | null
  /** Who reached out, relative to the viewer: "You" | a first name | "". Outbound only. */
  actor?: string
  /** Set on inbound events — the pane-2 reply card the paralegal linked. */
  reply?: { id: number; from_name: string; subject: string; source_date: string | null }
}

/** A pane-2 card the paralegal can hand-link to a unit as the reply. */
export type ReplyCandidate = { id: number; subject: string; from_name: string; source_date: string | null }

export type ArtifactDetail = Artifact & {
  cached_content: string
  body_html: string
  /** CIC Approvals console snapshot, present only on contract-memo artifacts. */
  cic_approvals?: { groups?: unknown[]; footer_actions?: unknown[] }
  /** Outbound communication trail keyed by console unit_key (category 1 MVP). */
  cic_comm?: Record<string, CommEvent[]>
  /** CIC workflow stage: 'intake' | 'ready_for_work'. */
  cic_stage?: string
  cached_file_url: string | null
  cached_file_size: number | null
  comments: Array<Record<string, unknown>>
  redlines: Array<Record<string, unknown>>
  /** Pipeline v2 attachment_markup step output. Includes the LLM's
   *  characterization for each redline/comment (raw docx-parsed
   *  `comments` / `redlines` fields above don't). When present, the
   *  pane-3 MarkupTable prefers `attachment_markup.redlines/comments`
   *  because they carry the NOTE column data. Null / missing when
   *  the AI step hasn't run yet — falls back to raw arrays. */
  attachment_markup?: {
    summary?: string
    redlines?: Array<Record<string, unknown>>
    comments?: Array<Record<string, unknown>>
    markup_issues?: unknown[]
    markup_obligations?: unknown[]
  } | null
  to_emails: string[]
  cc_emails: string[]
  source_modified_at: string | null
  last_seen_at: string | null
  source_status: string
  content_hash: string
  content_storage_mode: 'cached' | 'on_demand'
  message_id_header: string
  gmail_thread_id: string | null
  source_account_email: string
  duplicates: Array<{ id: number; source_account_email: string }>
  matter_assignments: Array<{ matter: number; assigned_by: 'ai' | 'user'; confidence: number | null; assigned_at: string }>
  /** Ken 2026-07-03: For documents referenced by URL in some email's
   *  body (i.e., an EmailDocLink target), the referring email's
   *  sender/recipient metadata so pane-3's "Sent by / Sent to /
   *  Copied" block can render even though the doc's own
   *  from_email/to_emails/cc_emails are empty. Scoped to the current
   *  matter's linking email when `?matter=N` is provided. Null when
   *  the artifact has no incoming EmailDocLink references. */
  linked_from?: {
    link_id: number
    email_id: number
    email_composite_id: string | null
    email_subject: string
    from_email: string
    from_name: string
    to_emails: string[]
    cc_emails: string[]
    source_date: string | null
  } | null
  /** Tenant client_context: {firm_name, colleagues[{name,email,role,notes}]}.
   *  Sourced from Client.context. Pane-3 uses this to resolve sender/
   *  recipient email addresses to display names. */
  client_context?: {
    firm_name?: string
    colleagues?: Array<{ name: string; email: string; role?: string; notes?: string }>
    [k: string]: unknown
  }
  /** Work Product (Heather 2026-08-05): the current user's WorkProduct
   *  flag id for this email, or null if unmarked. Drives the pane-3
   *  "Mark as work product" button. */
  my_work_product_id?: number | null
}

export type PromptSlot = {
  id: number
  name: string
  description: string
  level: number
  output_schema: Record<string, unknown>
  /** True for slots that exist for prompt iteration only (no
   *  ComputationStep references them; never auto-fires; runs go to
   *  SandboxRun, not LlmCallLog). */
  is_sandbox: boolean
  version_count: number
  production_version_id: number | null
  created_at: string
  updated_at: string
}

export type PromptVersion = {
  id: number
  slot: number
  slot_name: string
  version: number
  content: string
  model: string
  labels: string[]
  commit_message: string
  created_by: number | null
  created_by_username: string | null
  created_at: string
  var_warnings: string[]
}

export type ComputationStep = {
  id: number
  name: string
  slot: number
  slot_name: string
  level: number
  prompt_version_label: string
  backend: string
  // v1 kinds + v2 kinds (pipeline v2 fans out per_email_issue).
  fanout_kind:
    | 'none'
    | 'per_matter' | 'per_open_matter' | 'matter_only'
    | 'per_email_issue'
    | 'per_email_issue_per_open_matter'
    | 'per_attachment_per_email_issue'
  parent_ids: number[]
  child_ids: number[]
  created_at: string
}

export type ComputationRun = {
  id: number
  artifact: number
  triggered_by: string
  started_at: string
  completed_at: string | null
  status: string
  notes: string
}

export type ComputationResult = {
  id: number
  artifact: number
  step: number
  step_name: string
  run: number | null
  prompt_version: number
  prompt_version_number: number
  output: Record<string, unknown>
  status: string
  is_stale: boolean
  error_message: string
  computed_at: string
  updated_at: string
  latency_ms: number
}

export type LlmCallLog = {
  id: number
  client: number
  artifact: number | null
  step: number | null
  prompt_version: number | null
  model: string
  artifact_subject: string
  artifact_composite_id: string
  artifact_type: string
  artifact_from_email: string
  step_name: string
  input: string
  output: string
  input_tokens: number
  output_tokens: number
  latency_ms: number
  status: string
  error_message: string
  /** Prompt-render issues caught at template-substitution time (not LLM
   *  errors). Empty array when the prompt rendered cleanly. */
  render_issues: string[]
  created_at: string
}

export type AuditLog = {
  id: number
  user: number | null
  user_username: string | null
  action: string
  target_type: string
  target_id: string
  old_value: Record<string, unknown> | null
  new_value: Record<string, unknown> | null
  note: string
  created_at: string
}

type Page<T> = { count: number; next: string | null; previous: string | null; results: T[] }

const list =
  <T,>(path: string) =>
  async (params: Record<string, string | number | boolean> = {}): Promise<T[]> => {
    const r = await api.get<Page<T>>(path, { params })
    return r.data.results
  }

const detail =
  <T,>(path: string) =>
  async (id: number | string, params: Record<string, string | number | boolean> = {}): Promise<T> => {
    const r = await api.get<T>(`${path}${id}/`, { params })
    return r.data
  }

export type MatterCounter = { issues: number; attachments: number; new: boolean; last_activity?: string | null }

export const matters = {
  list: list<Matter>('/matters/'),
  detail: detail<Matter>('/matters/'),
  // Per-matter activity counts since a global time border (sidebar counters).
  // params is one of {hours}, {days}, or {since:<iso>}; matters with no signal
  // are omitted (treat as zeros).
  counters: (params: Record<string, string | number>) =>
    api.get<Record<string, MatterCounter>>('/matters/counters/', { params })
      .then(r => r.data),
  // Corpus border — most recent email arrival (max source_date), tenant-scoped.
  corpusEdge: () =>
    api.get<{ corpus_edge: string | null; last_sync_at: string | null; in_flight: number }>('/matters/corpus-edge/').then(r => r.data),
  create: async (payload: Partial<Matter>): Promise<Matter> => {
    await ensureCsrf()
    const r = await api.post('/matters/', payload)
    return r.data
  },
  update: async (id: number, payload: Partial<Matter>): Promise<Matter> => {
    await ensureCsrf()
    const r = await api.patch(`/matters/${id}/`, payload)
    return r.data
  },
  // Pipeline v2: on-demand matter summary. Returns paragraph + bullets
  // generated fresh against the matter's current state. No cache — per
  // Ken's spec, fresh each open. Latency = one LLM call.
  generateSummary: async (id: number): Promise<MatterSummaryResponse> => {
    await ensureCsrf()
    const r = await api.post(`/matters/${id}/summary/`)
    return r.data
  },
}

// Pipeline v2 types
export type MatterSummaryResponse = {
  paragraph_summary: string
  bullet_summary: string[]
  experimental_summary: string
  generated_at: string
  prompt_version: number
  matter_id: number
}

export type EmailIssue = {
  id: number
  composite_id: string  // 'email:<pk>' — globally unique across future issue types
  artifact: number
  artifact_subject: string
  artifact_source_date: string | null
  issue_index: number  // 1-based per-artifact position
  description: string
  source_excerpts: string[]
  filing_decision: '' | 'file_issue_with_existing_matter' | 'create_new'
  filing_confidence: number | null
  filing_reasoning: string
  matter: number | null
  matter_name: string | null
  summary: string
  obligations: Array<{
    direction: string
    assigned_to: string
    assigned_by: string
    description: string
    due_date: string | null
    source_excerpts: string[]
    issue_id?: string
  }>
  created_at: string
  updated_at: string
}

export type IssueMatchAssessment = {
  id: number
  issue: number
  matter: number
  matter_name: string
  does_belong: boolean
  confidence: number
  reason: string
  created_at: string
}

export const emailIssues = {
  list: list<EmailIssue>('/email-issues/'),
  detail: detail<EmailIssue>('/email-issues/'),
  byArtifact: async (artifactId: number): Promise<EmailIssue[]> => {
    const r = await api.get<{ results: EmailIssue[] }>('/email-issues/', { params: { artifact: artifactId, page_size: 100 } })
    return r.data.results || (r.data as unknown as EmailIssue[])
  },
  byMatter: async (matterId: number): Promise<EmailIssue[]> => {
    const r = await api.get<{ results: EmailIssue[] }>('/email-issues/', { params: { matter: matterId, page_size: 200 } })
    return r.data.results || (r.data as unknown as EmailIssue[])
  },
}

export const issueMatchAssessments = {
  list: list<IssueMatchAssessment>('/issue-match-assessments/'),
  byIssue: async (issueId: number): Promise<IssueMatchAssessment[]> => {
    const r = await api.get<{ results: IssueMatchAssessment[] }>('/issue-match-assessments/', { params: { issue: issueId, page_size: 100 } })
    return r.data.results || (r.data as unknown as IssueMatchAssessment[])
  },
}
export type ArtifactPage = {
  count: number
  next: string | null
  previous: string | null
  results: Artifact[]
}
export const artifacts = {
  list: list<Artifact>('/artifacts/'),
  detail: detail<ArtifactDetail>('/artifacts/'),
  // Returns the full paginated page (count + next/previous links + results),
  // unlike list() which strips to just results[].
  page: async (params: Record<string, string | number | boolean | undefined> = {}): Promise<ArtifactPage> => {
    const cleaned: Record<string, string | number | boolean> = {}
    for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== '') cleaned[k] = v
    const r = await api.get<ArtifactPage>('/artifacts/', { params: cleaned })
    return r.data
  },
  ignore: async (ids: number[], reason = 'manual'): Promise<{ updated: number; rejected: number }> => {
    await ensureCsrf()
    const r = await api.post('/artifacts/ignore/', { artifact_ids: ids, reason })
    return r.data
  },
  unignore: async (ids: number[]): Promise<{ updated: number; rejected: number }> => {
    await ensureCsrf()
    const r = await api.post('/artifacts/unignore/', { artifact_ids: ids })
    return r.data
  },
  // Log an outbound communication from the pre-GC intake console. The Gmail compose
  // is opened client-side; this records the action + a Mistral stub of the ask so the
  // per-unit trail shows what went out. Returns the created event.
  logContractComm: async (
    artifactId: number,
    payload: {
      unit_key: string
      unit_label?: string
      recipients?: { label: string; email?: string }[]
      agreement_name?: string
      sandbox?: boolean
    },
  ): Promise<CommEvent> => {
    await ensureCsrf()
    const r = await api.post(`/artifacts/${artifactId}/contract-comm/`, payload)
    return r.data
  },
  // Pane-2 cards in the memo's matter(s) that could be a reply to hand-link to a unit.
  replyCandidates: async (artifactId: number): Promise<ReplyCandidate[]> => {
    const r = await api.get(`/artifacts/${artifactId}/reply-candidates/`)
    return r.data.candidates || []
  },
  // Hand-link one pane-2 reply card to a console unit as an inbound event.
  linkReply: async (
    artifactId: number,
    payload: { unit_key: string; unit_label?: string; reply_artifact_id: number },
  ): Promise<CommEvent> => {
    await ensureCsrf()
    const r = await api.post(`/artifacts/${artifactId}/link-reply/`, payload)
    return r.data
  },
  // Office Priorities chat — ask about the agreements on the plate (runs on the ZDR
  // provider). Stateless: send the running transcript, get back the assistant's reply.
  priorityChat: async (messages: { role: string; content: string }[]): Promise<string> => {
    await ensureCsrf()
    const r = await api.post('/artifacts/priorities-chat/', { messages })
    return r.data.reply || ''
  },
  // Toggle a CIC agreement's stage intake ↔ ready_for_work ("Ready for Work" button).
  promoteReady: async (artifactId: number): Promise<string> => {
    await ensureCsrf()
    const r = await api.post(`/artifacts/${artifactId}/promote-ready/`, {})
    return r.data.cic_stage || 'intake'
  },
  // "What's New" chat — ZDR Q&A over recent EmailIssue summaries (studio-only for now).
  whatsNewChat: async (messages: { role: string; content: string }[]): Promise<string> => {
    await ensureCsrf()
    const r = await api.post('/artifacts/whats-new-chat/', { messages })
    return r.data.reply || ''
  },
  // "Proposed Agreements" pane-1 gutter — compact read-only list of in-flight CIC
  // submissions. Per-user opt-in (see User.cic_agreements_gutter). Returns [] when off.
  agreementsGutter: async (): Promise<AgreementGutterRow[]> => {
    const r = await api.get('/contract-agreements-gutter/')
    return r.data || []
  },
}

export type AgreementGutterRow = {
  id: number
  name: string
  submitter: string
  stage: string
  url: string
  overdue: boolean
}

// Public (passwordless) IT-reviewer endpoints — the token is the credential.
export type ITReviewState = {
  agreement: { name: string; counterparty: string; purpose: string; kind: string; request_type: string }
  it_review: {
    required: boolean | null; systems_access: boolean | null; externally_hosted: boolean | null
    sensitive_data: boolean | null; rationale: string; excerpts: { signal: string; quote: string }[]
  }
  has_text: boolean
}
export const itReview = {
  state: async (token: string): Promise<ITReviewState> => {
    const r = await api.get(`/contract-it/${token}/`)
    return r.data
  },
  reply: async (token: string, messages: { role: string; content: string }[], standards: string): Promise<string> => {
    const r = await api.post(`/contract-it/${token}/reply/`, { messages, standards })
    return r.data.reply || ''
  },
}
export const promptSlots = {
  list: list<PromptSlot>('/prompt-slots/'),
  detail: detail<PromptSlot>('/prompt-slots/'),
  vars: async (slotId: number): Promise<{ vars: Record<string, string> }> => {
    const r = await api.get(`/prompt-slots/${slotId}/vars/`)
    return r.data
  },
  /** Create a new prompt slot. Currently used for sandbox slots; production
   *  slots are still seeded via the seed_real_dag management command. */
  create: async (payload: Partial<PromptSlot>): Promise<PromptSlot> => {
    await ensureCsrf()
    const r = await api.post('/prompt-slots/', payload)
    return r.data
  },
  update: async (id: number, payload: Partial<PromptSlot>): Promise<PromptSlot> => {
    await ensureCsrf()
    const r = await api.patch(`/prompt-slots/${id}/`, payload)
    return r.data
  },
  remove: async (id: number): Promise<void> => {
    await ensureCsrf()
    await api.delete(`/prompt-slots/${id}/`)
  },
  /** Ken 2026-07-06 — download every live production prompt as a zip
   *  of .txt files, one per slot. Backend endpoint filters non-sandbox
   *  + non-archived slots that have a 'production' PromptVersion and
   *  streams a .zip response. Returns the Blob for the caller to
   *  trigger a download from. */
  exportProduction: async (): Promise<Blob> => {
    const r = await api.get('/prompt-slots/export_production/', {
      responseType: 'blob',
    })
    return r.data
  },
}
export const promptVersions = {
  list: list<PromptVersion>('/prompt-versions/'),
  detail: detail<PromptVersion>('/prompt-versions/'),
  bySlot: (slotId: number) => list<PromptVersion>('/prompt-versions/')({ slot: slotId }),
  // Studio Test Run uses this to surface sandbox-slot versions as
  // cross-slot pin options for any production step. Returns versions
  // whose slot has is_sandbox=True.
  sandboxOnly: () => list<PromptVersion>('/prompt-versions/')({ is_sandbox: true }),
  create: async (payload: Partial<PromptVersion>): Promise<PromptVersion> => {
    await ensureCsrf()
    const r = await api.post('/prompt-versions/', payload)
    return r.data
  },
  diff: async (ids: number[]): Promise<DiffResponse> => {
    const r = await api.get('/prompt-versions/diff/', { params: { ids: ids.join(',') } })
    return r.data
  },
}

export type PromptTestSet = {
  id: number
  client: number
  slot: number
  slot_name: string
  name: string
  description: string
  artifacts: number[]
  artifact_count: number
  expected_outputs: Record<string, unknown>
  created_by: number | null
  created_at: string
  updated_at: string
}

export const promptTestSets = {
  list: list<PromptTestSet>('/prompt-test-sets/'),
  detail: detail<PromptTestSet>('/prompt-test-sets/'),
  bySlot: (slotId: number) => list<PromptTestSet>('/prompt-test-sets/')({ slot: slotId }),
  create: async (payload: { slot: number; name: string; description?: string; artifacts?: number[]; expected_outputs?: Record<string, unknown> }): Promise<PromptTestSet> => {
    await ensureCsrf()
    const r = await api.post('/prompt-test-sets/', payload)
    return r.data
  },
  update: async (id: number, payload: Partial<{ name: string; description: string; artifacts: number[]; expected_outputs: Record<string, unknown> }>): Promise<PromptTestSet> => {
    await ensureCsrf()
    const r = await api.patch(`/prompt-test-sets/${id}/`, payload)
    return r.data
  },
  remove: async (id: number) => {
    await ensureCsrf()
    await api.delete(`/prompt-test-sets/${id}/`)
  },
}

export type TestSetRunResult = {
  artifact_id: number
  artifact_subject: string
  rendered_input: string
  output_text: string
  output_parsed: unknown
  input_tokens: number
  output_tokens: number
  latency_ms: number
  expected: unknown
  matches_expected: boolean | null
}

export async function runTestSet(versionId: number, testSetId: number): Promise<{
  test_set: string
  prompt_version_id: number
  // Ken 2026-07-12 — persisted TestSetRun row id, used by the Download
  // zip button so the server-side exporter can look up the SandboxRun
  // rows this run produced. Backend has always returned it; type just
  // wasn't declaring it.
  test_set_run_id?: number
  status?: string
  results: TestSetRunResult[]
}> {
  await ensureCsrf()
  const r = await api.post(`/prompt-versions/${versionId}/run-test-set/`, { test_set: testSetId })
  return r.data
}
export const steps = {
  list: list<ComputationStep>('/steps/'),
  detail: detail<ComputationStep>('/steps/'),
  create: async (payload: Partial<ComputationStep>): Promise<ComputationStep> => {
    await ensureCsrf()
    const r = await api.post('/steps/', payload)
    return r.data
  },
  update: async (id: number, payload: Partial<ComputationStep>): Promise<ComputationStep> => {
    await ensureCsrf()
    const r = await api.patch(`/steps/${id}/`, payload)
    return r.data
  },
  remove: async (id: number) => {
    await ensureCsrf()
    await api.delete(`/steps/${id}/`)
  },
}
export const stepDeps = {
  list: list<{ id: number; parent: number; child: number }>('/step-dependencies/'),
  create: async (parent: number, child: number): Promise<{ id: number }> => {
    await ensureCsrf()
    const r = await api.post('/step-dependencies/', { parent, child })
    return r.data
  },
  remove: async (id: number) => {
    await ensureCsrf()
    await api.delete(`/step-dependencies/${id}/`)
  },
}
export const runs = { list: list<ComputationRun>('/runs/') }
export const results = { list: list<ComputationResult>('/results/') }
export const llmCalls = {
  list: list<LlmCallLog>('/llm-calls/'),
  /** Returns the full paginated envelope (count + results) so a caller
   *  can render Prev/Next pagers. The plain `.list()` returns just
   *  results[] for callers that don't care about pagination. */
  listPaged: async (params: Record<string, string | number> = {}): Promise<{ count: number; results: LlmCallLog[] }> => {
    const r = await api.get<{ count: number; results: LlmCallLog[] }>('/llm-calls/', { params })
    return { count: r.data.count, results: r.data.results }
  },
}

// ── Interaction traces (Studio → Searches) ──────────────────────────────
// The app's only client-posted-event path: a genuine end-user's browser POSTs a
// trace when they trigger a tracked pattern on Main/Stats/Playbook. Read side is
// superuser-only (the Searches table).
export type TracePage = 'main' | 'stats' | 'playbook' | 'privacy'
export type TracePattern = 'top_left' | 'sort' | 'visit' | 'wall_create' | 'refile'
export type TraceStep = { type: 'folder' | 'matter' | 'card'; id: number | null; label: string; at?: string }
export type InteractionTrace = {
  id: number
  client: number | null
  client_code: string
  user: number | null
  user_name: string
  page: TracePage
  pattern: TracePattern
  trigger: { kind?: string; text?: string; control?: string; value?: string; match?: string; mode?: string; source?: string }
  steps: TraceStep[]
  created_at: string
}
export const interactionTraces = {
  listPaged: async (params: Record<string, string | number> = {}): Promise<Page<InteractionTrace>> => {
    const r = await api.get<Page<InteractionTrace>>('/interaction-traces/', { params })
    return r.data
  },
  create: async (body: { page: TracePage; pattern: TracePattern; trigger: Record<string, unknown>; steps: TraceStep[] }) => {
    await ensureCsrf()
    return api.post<InteractionTrace>('/interaction-traces/', body).then((r) => r.data)
  },
  // Cheap poll for the Studio nav 'new traces' pip.
  latest: async (): Promise<{ latest_at: string | null; count: number }> => {
    const r = await api.get<{ latest_at: string | null; count: number }>('/interaction-traces/latest/')
    return r.data
  },
}

// ── "Add material from Drive" — attorney searches their own Drive, attaches
// picked docs to a matter (see apps/sync/views.py drive_search / drive_attach).
export type DriveHit = {
  file_id: string
  name: string
  mime_type: string
  kind: 'doc' | 'sheet' | 'pdf' | 'word' | 'file'
  modified_time: string | null
  editor: string
  already_attached: boolean
}
export type DriveFolder = { id: string; name: string }

export const drive = {
  search: (q: string, matter: number) =>
    api.get<{ connected: boolean; results: DriveHit[]; error?: string }>(
      '/drive/search/', { params: { q, matter } },
    ).then((r) => r.data),
  // Browse ONE folder of the user's own Drive (folder='root' = My Drive top).
  // Powers the folder-tree side of the picker (Ken 2026-09-02).
  browse: (folder: string, matter: number) =>
    api.get<{ connected: boolean; folder: string; folders: DriveFolder[]; files: DriveHit[]; error?: string }>(
      '/drive/browse/', { params: { folder, matter } },
    ).then((r) => r.data),
  attach: async (matter: number, fileIds: string[]) => {
    await ensureCsrf()
    return api.post<{ attached: { id: number; subject: string; file_id: string }[]; count: number }>(
      '/drive/attach/', { matter, file_ids: fileIds },
    ).then((r) => r.data)
  },
}

export type SandboxRun = {
  id: number
  slot: number
  slot_name: string
  prompt_version: number | null
  prompt_version_number: number | null
  artifact: number
  artifact_subject: string
  artifact_composite_id: string | null
  test_set_run: number | null
  source: 'dry_run' | 'test_set' | 'pipeline_test' | 'sandbox_slot'
  rendered_input: string
  output_text: string
  output_parsed: unknown
  input_tokens: number
  output_tokens: number
  latency_ms: number
  status: 'success' | 'error' | 'timeout'
  error_message: string
  created_by: number | null
  created_by_username: string | null
  created_at: string
}

export const sandboxRuns = {
  list: list<SandboxRun>('/sandbox-runs/'),
}

export type TestSetRun = {
  id: number
  test_set: number
  test_set_name: string
  prompt_version: number | null
  prompt_version_number: number | null
  started_at: string
  completed_at: string | null
  status: 'running' | 'completed' | 'partial' | 'failed'
  artifact_count: number
  success_count: number
  error_count: number
  notes: string
  created_by: number | null
  created_by_username: string | null
}

export const testSetRuns = {
  list: list<TestSetRun>('/test-set-runs/'),
}

export type ManualEditTargetType = 'matter' | 'artifact'

export type ManualEditEntry = {
  id: number
  client: number | null
  user: number | null
  user_username: string | null
  target_type: ManualEditTargetType
  target_id: number
  target_label: string
  field: string
  old_value: unknown
  new_value: unknown
  reason: string
  created_at: string
}

/** Save a user edit to an AI-generated field. Server applies the change
 *  to the canonical row AND records a ManualEdit audit row. Returns the
 *  persisted value + the new audit row's id. */
export async function manualEdit(payload: {
  target_type: ManualEditTargetType
  target_id: number
  field: string
  value: unknown
  reason?: string
}): Promise<{
  manual_edit_id: number
  target_type: ManualEditTargetType
  target_id: number
  field: string
  value: unknown
  detail?: string
}> {
  await ensureCsrf()
  const r = await api.post('/manual-edit/', payload)
  return r.data
}

export const manualEdits = {
  list: list<ManualEditEntry>('/manual-edits/'),
}
export const auditLog = { list: list<AuditLog>('/audit-log/') }
export const clients = {
  list: list<ClientSettings>('/clients/'),
  detail: detail<ClientSettings>('/clients/'),
  update: async (id: number, payload: Partial<ClientSettings>): Promise<ClientSettings> => {
    await ensureCsrf()
    const r = await api.patch(`/clients/${id}/`, payload)
    return r.data
  },
}

export type DiffWordOp = {
  line_a: number
  line_b: number
  a_words: string[]
  b_words: string[]
  opcodes: [string, number, number, number, number][]
}

export type DiffEntry = {
  base_version_id: number
  other_version_id: number
  lines_a: string[]
  lines_b: string[]
  opcodes: [string, number, number, number, number][]
  word_ops: DiffWordOp[]
  unified: string
}

export type DiffResponse = {
  versions: { id: number; version: number; slot: string; labels: string[]; commit_message: string; content: string }[]
  diffs: DiffEntry[]
}

export async function dryRunPrompt(opts: {
  prompt_version_id: number
  artifact_id: number
  mock_upstream?: Record<string, unknown>
}): Promise<{
  prompt_version: { id: number; version: number; slot: string; content: string }
  artifact: { id: number; subject: string; client_seq: number }
  rendered_input: string
  output_text: string
  output_parsed: unknown
  input_tokens: number
  output_tokens: number
  latency_ms: number
  model: string
}> {
  await ensureCsrf()
  const r = await api.post('/dry-run-prompt/', opts)
  return r.data
}

export async function promotePromptVersion(versionId: number, label = 'production'): Promise<{ outdated_count?: number; stale_count?: number }> {
  // Server returns `outdated_count` post-decoupling; older builds may
  // still ship `stale_count`. Caller renders whichever is present.
  await ensureCsrf()
  const r = await api.post('/promote-prompt/', { version_id: versionId, label })
  return r.data
}

// ─── Outdated / stale counts and the deliberate-stale flow ──────────
//
// See docs/specs/outdated-vs-stale.md. "Outdated" = result was computed
// under a non-current prompt version (derived). "Stale" = explicitly
// queued for rerun (DB flag, only set deliberately).

export type StepStaleCount = {
  step_id: number
  step_name: string
  slot_name: string
  level: number
  production_version: number | null
  production_version_id: number | null
  computation_result: { total: number; stale: number; outdated: number }
  matter_step_result: { total: number; stale: number; outdated: number }
}

export async function fetchStepOutdatedCounts(clientCode?: string): Promise<{ steps: StepStaleCount[]; client_code: string | null }> {
  const params = clientCode ? `?client_code=${encodeURIComponent(clientCode)}` : ''
  const r = await api.get(`/steps/outdated-counts/${params}`)
  return r.data
}

export type MarkStaleRequest = {
  client_code: string
  step_names: string[]
  matter_ids?: number[]
  artifact_ids?: number[]
  source_date_from?: string
  source_date_to?: string
  outdated_only?: boolean
  dry_run?: boolean
}

export type MarkStaleResponse = {
  counts: { computation_result: number; matter_step_result: number }
  to_flip: { computation_result: number; matter_step_result: number }
  by_step: { step: string; cr_to_flip: number; msr_to_flip: number }[]
  flipped: { computation_result: number; matter_step_result: number } | null
}

export async function markResultsStale(req: MarkStaleRequest): Promise<MarkStaleResponse> {
  await ensureCsrf()
  const r = await api.post('/results/mark-stale/', req)
  return r.data
}

// Per-artifact step drift summary, used by Studio > Artifacts to render
// a "v3→v4" chip on a row when any of its step results were computed
// under a non-current prompt version.
export type ArtifactDriftSummary = {
  computation_result: { total: number; stale: number; outdated: number }
  matter_step_result:  { total: number; stale: number; outdated: number }
  steps: Array<{
    step_name: string
    result_version_id: number | null
    production_version_id: number | null
    is_outdated: boolean
    is_stale: boolean
    result_kind: 'computation_result' | 'matter_step_result'
  }>
}

export async function fetchArtifactResultDrift(ids: number[]): Promise<{ by_artifact: Record<string, ArtifactDriftSummary> }> {
  if (ids.length === 0) return { by_artifact: {} }
  const r = await api.get(`/artifacts/result-drift/?ids=${ids.join(',')}`)
  return r.data
}

// Resolve composite_ids the AI mentioned in chat prose to clickable
// links. Privacy-respecting on the backend — codes the user can't see
// drop out of the result map silently.
export type CompositeResolution = {
  kind: 'matter' | 'artifact'
  id: number
  name?: string
}
export async function resolveCompositeIds(codes: string[]): Promise<{ resolutions: Record<string, CompositeResolution> }> {
  if (codes.length === 0) return { resolutions: {} }
  await ensureCsrf()
  const r = await api.post('/chat/lookup-composite/', { codes })
  return r.data
}

export async function runPipeline(artifactId: number, opts: { force?: boolean; sync?: boolean } = {}): Promise<{ status: string; run_id?: number }> {
  await ensureCsrf()
  const r = await api.post('/run-pipeline/', {
    artifact_id: artifactId,
    force: opts.force ?? false,
    async: !opts.sync,
  })
  return r.data
}

// Pipeline v2 entry point. Studio-editor gated. Async=true enqueues onto
// the artifact's tenant matter_decisions queue; async=false runs inline
// (useful with dry_run=true for inspection without spending tokens).
export type RunPipelineV2Result = {
  status: string
  artifact_id?: number
  task_id?: string
  // Sync mode + dry_run also yields the orchestrator plan / result fields
  phases_completed?: string[]
  issues_created?: number
  matters_created?: number
  matters_filed_to?: number[]
  errors?: string[]
  planned?: string[]
}
export async function runPipelineV2(
  artifactId: number,
  opts: { sync?: boolean; dryRun?: boolean } = {},
): Promise<RunPipelineV2Result> {
  await ensureCsrf()
  const r = await api.post('/run-pipeline-v2/', {
    artifact_id: artifactId,
    async: !opts.sync,
    dry_run: opts.dryRun ?? false,
  })
  return r.data
}

export async function runMatterPipeline(matterId: number, opts: { force?: boolean; sync?: boolean } = {}): Promise<{ status: string; run_id?: number; detail?: string }> {
  await ensureCsrf()
  const r = await api.post(`/run-matter-pipeline/${matterId}/`, {
    force: opts.force ?? false,
    async: !opts.sync,
  })
  return r.data
}

export async function runPipelineBulk(artifactIds: number[], opts: { force?: boolean } = {}): Promise<{ enqueued: number; rejected: number }> {
  await ensureCsrf()
  const r = await api.post('/run-pipeline-bulk/', {
    artifact_ids: artifactIds,
    force: opts.force ?? false,
  })
  return r.data
}

export type ImportSource = 'emails' | 'docs' | 'attachments'
export type ImportReport = Record<ImportSource, { created: number; updated: number; skipped: number; total: number } | undefined>

export async function importFromV1(opts: {
  client_code: string
  legacy_client_id: number
  sources: ImportSource[]
  limit?: number
  offset?: number
  /** ISO date (YYYY-MM-DD) or full datetime; only import rows with
   *  internal_date / modified_time >= this. Mirrors the CLI flag. */
  since?: string | null
  /** Same shape as `since`, but the upper bound (exclusive). */
  until?: string | null
}): Promise<{ client: string; legacy_client_id: number; limit: number; offset: number;
              since: string | null; until: string | null; report: ImportReport }> {
  await ensureCsrf()
  const r = await api.post('/imports/v1/', opts)
  return r.data
}

/** Per-matter result row inside a per_open_matter fanout response. */
export type PipelineTestMatterAssessment = {
  matter_id: number
  matter_code: string  // e.g. "M-013"
  matter_name: string
  matter_status: string
  matter_stage: string
  rendered_input: string
  output_parsed: unknown
  output_text: string
  input_tokens: number
  output_tokens: number
  latency_ms: number
  status: 'success' | 'error'
  error_message: string
  /** Ken 2026-07-19 entity gate — populated only in v2 fanout.
   *  `true` when the deterministic Fan-Out gate overrode the LLM's
   *  does_belong=true verdict to false (empty entities_found_in_matter
   *  OR every entry was substantially a generic term). output_parsed
   *  will already show the gated values; this flag just lets the UI
   *  badge the row so the operator sees the override happened. */
  entity_gated?: boolean
  entity_gate_reason?: string
}

export type PipelineTestStepResponse = {
  step_name: string
  slot_name: string
  prompt_version: { id: number; version: number; labels: string[]; is_pinned: boolean }
  rendered_input?: string
  output_parsed: unknown
  output_text?: string
  input_tokens?: number
  output_tokens?: number
  latency_ms?: number
  model: string
  // Skip-applicability marker for non-applicable artifact/step combos
  // (e.g. evaluate_image_content on a non-image artifact). Set by the
  // backend; UI shows a "skipped" badge instead of an output block.
  skipped?: boolean
  skip_reason?: string
  // True when L5+ step ran against an in-memory virtual matter
  // synthesized for L4's create_new path (matter is NOT in the DB).
  // UI shows a "virtual matter (not saved)" badge.
  virtual_matter?: boolean
  // Per_open_matter fanout fields. Present only when the step's
  // fanout_kind is 'per_open_matter' (currently L3 assess_matter_match).
  fanout?: boolean
  matter_assessments?: PipelineTestMatterAssessment[]
  summary?: {
    matters_evaluated: number
    does_belong_true: number
    errors: number
    total_latency_ms: number
  }
  // v2 pipeline flag + issue context (Ken 2026-07-11). Populated when
  // the backend served the call through the v2 code path — surfaces
  // which issue the fanout ran against for per-(issue × matter) steps.
  pipeline_version?: 'v2'
  issue_index?: number | null
  issue_description?: string
  /** Ken 2026-07-19 anti-echo — populated only in v2 non-fanout
   *  responses (L3 summarize_artifact + L6 update_matter_summary).
   *  Any person-name string here was present in the LLM's raw output
   *  but not traceable to the artifact's own text, so the pipeline
   *  (both live + Test Run) stripped it. `kw:<value>` prefix means a
   *  whole keyword was dropped from matter_keywords. Empty array =
   *  no fabrications caught. */
  anti_echo_removed?: string[]
  // Ken 2026-07-12 — where the issue came from. `session` is the honest
  // test case (fresh L3 output stepped through in this Test Run
  // session); `db` reads a persisted EmailIssue row from a prior REAL
  // pipeline run, which will be under an OLDER L3 prompt version and
  // is a trap when the operator is iterating on L3; `synthesized`
  // means we fabricated a virtual issue from the artifact body
  // because neither of the above existed.
  issue_source?: 'session' | 'db' | 'synthesized' | null
  virtual_issue?: boolean
}

export async function pipelineTestStep(opts: {
  artifact_id: number
  step_id: number
  upstream_outputs?: Record<string, unknown>
  prompt_version_id?: number
}): Promise<PipelineTestStepResponse> {
  await ensureCsrf()
  const r = await api.post('/pipeline-test/step/', opts)
  return r.data
}

export async function runPipelineForMatter(matterId: number, opts: { force?: boolean } = {}): Promise<{ enqueued: number; matter_id: number }> {
  await ensureCsrf()
  const r = await api.post(`/matters/${matterId}/run-pipeline/`, {
    force: opts.force ?? false,
  })
  return r.data
}

export async function runStale(): Promise<{ enqueued: number }> {
  await ensureCsrf()
  const r = await api.post('/run-stale/', {})
  return r.data
}

export async function dagValidate(): Promise<{ ok: boolean; error?: string; levels?: Record<string, number>; step_count?: number }> {
  const r = await api.get('/dag-validate/')
  return r.data
}

// ─── Chat ────────────────────────────────────────────────────────────────────

export type ChatScopeKind = 'global' | 'matter' | 'artifact'

export type ChatToolCall = {
  name: string
  args: Record<string, unknown>
  result_preview: string
}

export type ChatPersistedMessage = {
  id: number
  role: 'user' | 'assistant' | 'tool'
  content: string
  tool_calls: ChatToolCall[] | null
  tool_call_id: string | null
  tool_name: string | null
  iterations: number | null
  input_tokens: number | null
  output_tokens: number | null
  latency_ms: number | null
  created_at: string
}

export type ChatContextMeter = {
  budget: number
  used_estimated: number
  used_reported: number
  trimmed: boolean
  dropped_count: number
  summarized_this_turn?: boolean
  has_summary?: boolean
  summary?: string
}

export type ChatHistoryResponse = {
  scope_kind: ChatScopeKind
  scope_id: number | null
  conversation_id: number | null
  messages: ChatPersistedMessage[]
  context: ChatContextMeter
}

export type ChatTurnResponse = {
  conversation_id: number
  scope_kind: ChatScopeKind
  scope_id: number | null
  user_message: ChatPersistedMessage
  assistant_message: ChatPersistedMessage
  iterations: number
  input_tokens: number
  output_tokens: number
  context: ChatContextMeter
}

export async function loadChatHistory(opts: {
  scope_kind?: ChatScopeKind
  scope_id?: number | null
}): Promise<ChatHistoryResponse> {
  const params: Record<string, string> = {}
  if (opts.scope_kind && opts.scope_kind !== 'global') {
    params.scope_kind = opts.scope_kind
    if (opts.scope_id != null) params.scope_id = String(opts.scope_id)
  }
  const r = await api.get('/chat/history/', { params })
  return r.data
}

export async function clearChatHistory(opts: {
  scope_kind?: ChatScopeKind
  scope_id?: number | null
}): Promise<{ cleared: boolean }> {
  await ensureCsrf()
  const params: Record<string, string> = {}
  if (opts.scope_kind && opts.scope_kind !== 'global') {
    params.scope_kind = opts.scope_kind
    if (opts.scope_id != null) params.scope_id = String(opts.scope_id)
  }
  const r = await api.delete('/chat/history/', { params })
  return r.data
}

export async function sendChat(opts: {
  message: string
  scope_kind?: ChatScopeKind
  scope_id?: number | null
  /** The artifact the user has selected in the UI right now. Sent
   *  separately from scope so a matter-scoped chat can know which
   *  artifact "this" refers to. */
  current_artifact_id?: number | null
}): Promise<ChatTurnResponse> {
  await ensureCsrf()
  const body: Record<string, unknown> = { message: opts.message }
  if (opts.scope_kind && opts.scope_kind !== 'global') {
    body.scope_kind = opts.scope_kind
    if (opts.scope_id != null) body.scope_id = opts.scope_id
  }
  if (opts.current_artifact_id != null) body.current_artifact_id = opts.current_artifact_id
  const r = await api.post('/chat/', body)
  return r.data
}

// ─── Streaming chat ──────────────────────────────────────────────────────────

export type ChatStreamEvent =
  | { type: 'chunk'; text: string }
  | { type: 'tool_call'; name: string; args: Record<string, unknown>; tool_call_id: string }
  | { type: 'tool_result'; name: string; preview: string; tool_call_id: string }
  | { type: 'done'; payload: ChatStreamDonePayload }
  | { type: 'error'; detail: string }

export type ChatStreamDonePayload = {
  conversation_id: number
  scope_kind: ChatScopeKind
  scope_id: number | null
  user_message_id: number
  assistant_message_id: number | null
  iterations: number
  input_tokens: number
  output_tokens: number
  context: ChatContextMeter
}

/**
 * Stream a chat turn from the server. Returns an async generator of
 * typed events. Backend pushes SSE frames (event: chunk/tool_call/...);
 * we parse them into a discriminated union the consumer can switch on.
 *
 * Usage:
 *   for await (const ev of sendChatStream({...})) {
 *     if (ev.type === 'chunk') append(ev.text)
 *     else if (ev.type === 'tool_call') showStatus(`calling ${ev.name}...`)
 *     else if (ev.type === 'done') invalidateHistory()
 *   }
 */
export async function* sendChatStream(opts: {
  message: string
  scope_kind?: ChatScopeKind
  scope_id?: number | null
  current_artifact_id?: number | null
  signal?: AbortSignal
}): AsyncGenerator<ChatStreamEvent, void, unknown> {
  await ensureCsrf()
  const body: Record<string, unknown> = { message: opts.message }
  if (opts.scope_kind && opts.scope_kind !== 'global') {
    body.scope_kind = opts.scope_kind
    if (opts.scope_id != null) body.scope_id = opts.scope_id
  }
  if (opts.current_artifact_id != null) body.current_artifact_id = opts.current_artifact_id

  // Get CSRF token so the POST passes Django middleware.
  const csrfToken = readCookie('csrftoken') || ''

  const resp = await fetch(`${API_BASE}/chat/stream/`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRFToken': csrfToken,
      'Accept': 'text/event-stream',
    },
    body: JSON.stringify(body),
    signal: opts.signal,
  })
  if (!resp.ok || !resp.body) {
    let detail = `${resp.status} ${resp.statusText}`
    try {
      const j = await resp.json()
      if (j?.detail) detail = j.detail
    } catch { /* not json */ }
    yield { type: 'error', detail }
    return
  }

  const reader = resp.body.getReader()
  const decoder = new TextDecoder()
  let buf = ''
  while (true) {
    const { value, done } = await reader.read()
    if (done) break
    buf += decoder.decode(value, { stream: true })
    // SSE frames are separated by \n\n.
    let frameEnd = buf.indexOf('\n\n')
    while (frameEnd >= 0) {
      const frame = buf.slice(0, frameEnd)
      buf = buf.slice(frameEnd + 2)
      const ev = parseSseFrame(frame)
      if (ev) yield ev
      frameEnd = buf.indexOf('\n\n')
    }
  }
}

function parseSseFrame(frame: string): ChatStreamEvent | null {
  let event = ''
  const dataLines: string[] = []
  for (const line of frame.split('\n')) {
    if (line.startsWith('event:')) event = line.slice(6).trim()
    else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim())
  }
  if (!event || dataLines.length === 0) return null
  let data: unknown
  try {
    data = JSON.parse(dataLines.join('\n'))
  } catch {
    return null
  }
  const d = data as Record<string, unknown>
  switch (event) {
    case 'chunk':
      return { type: 'chunk', text: String(d.text ?? '') }
    case 'tool_call':
      return {
        type: 'tool_call',
        name: String(d.name ?? ''),
        args: (d.args as Record<string, unknown>) ?? {},
        tool_call_id: String(d.tool_call_id ?? ''),
      }
    case 'tool_result':
      return {
        type: 'tool_result',
        name: String(d.name ?? ''),
        preview: String(d.preview ?? ''),
        tool_call_id: String(d.tool_call_id ?? ''),
      }
    case 'done':
      return { type: 'done', payload: d as unknown as ChatStreamDonePayload }
    case 'error':
      return { type: 'error', detail: String(d.detail ?? 'stream error') }
    default:
      return null
  }
}

function readCookie(name: string): string | null {
  const m = document.cookie.match(new RegExp(`(^|; )${name}=([^;]*)`))
  return m ? decodeURIComponent(m[2]) : null
}

// ─── Per-matter assessments (assess_matter_match results) ───────────────────

export type MatchAssessment = {
  matter_id: number
  matter_client_seq: number
  matter_name: string
  matter_status: string
  matter_stage: string
  does_belong: boolean | null
  confidence: number | null
  primary: boolean
  reason: string
  lifecycle_signal: string | null
  evidence_quote: string
  status: string
  computed_at: string
}

export async function getMatchAssessments(artifactId: number): Promise<{ count: number; assessments: MatchAssessment[] }> {
  const r = await api.get(`/artifacts/${artifactId}/match-assessments/`)
  return r.data
}

// ─── Matter step results (L4/L5 fanout outputs) ─────────────────────────────

export type MatterStepResult = {
  step_id: number
  step_name: string
  output: Record<string, unknown>
  status: string
  is_stale: boolean
  triggered_by_artifact_id: number | null
  prompt_version: number | null
  latency_ms: number
  computed_at: string
}

export async function getMatterStepResults(matterId: number): Promise<{ matter_id: number; results: MatterStepResult[] }> {
  const r = await api.get(`/matters/${matterId}/step-results/`)
  return r.data
}


// ─── Pre-seed matters (Ken 2026-07-19 dev ticket) ─────────────────
// Super-user tool to materialize known long-lived matters BEFORE
// ingest, so their mail files into them on the very next run instead
// of getting AI-discovered then refiled. See apps/matters/views.py.

export type PreseedMatter = {
  matter_id: number
  client_seq: number
  matter_name: string
  matter_type: string
  status: string
  description: string
  /** Authoritative keyword list (Matter.keywords array). Frontend was
   *  previously parsing keywords out of `description` via regex, but
   *  description isn't re-packed once the matter has real artifacts,
   *  so edits appeared to "disappear" on next open. Ken 2026-07-20 fix. */
  keywords: string[]
  folder_id: number | null
  folder_name: string | null
  artifact_count: number
  created_at: string
}

export type PreseedMatterInput = {
  matter_name: string
  matter_type: string  // matches Matter.matter_type choices
  keywords: string[]
  folder_id: number | null
}

// ─── Re-file (Ken 2026-07-21 memo) ─────────────────────────────
// User-triggered subtract-and-reroute of an artifact's issues from
// the currently-viewed matter to another matter (or a brand-new one).
// See apps/pipeline/runner_v2.refile_issues_to_matter.

export type RefileResult = {
  ok: boolean
  reason?: string
  artifact_id: number
  source_matter_id: number
  target_matter_id: number | null
  target_is_new: boolean
  moved_issue_ids: number[]
  moved_attachment_ids: number[]
  /** Celery task id for the async LLM followup chain (revise
   *  description, phase groupings, generate summary, etc.). Present
   *  when the followup enqueue succeeded. */
  followup_task_id?: string | null
  followup_enqueue_error?: string
  /** "Move these too": count of checked same-thread siblings moved, and
   *  any source matters auto-archived because they emptied. */
  also_moved?: number
  archived_matter_ids?: number[]
}

export type RefileSibling = {
  artifact_id: number
  emal: string
  subject: string
  source_date: string | null
  matter: { id: number; client_seq: number; name: string } | null
}
export async function refileSiblings(
  artifactId: number,
): Promise<{ thread_id: string; siblings: RefileSibling[] }> {
  const r = await api.get('/refile-siblings/', { params: { artifact_id: artifactId } })
  return r.data
}

export async function refileArtifact(payload: {
  artifact_id: number
  source_matter_id: number
  target_matter_id: number | null  // null = create new matter
  include_attachments: boolean
  also_artifact_ids?: number[]     // checked same-thread siblings to move too
}): Promise<RefileResult> {
  await ensureCsrf()
  const r = await api.post('/refile-artifact/', payload)
  return r.data
}


// ─── Thread-issue salvage suggestions (QA page) ───────────────────────
export type ThreadSuggestion = {
  id: number
  type: 'duplicate' | 'misfiled'
  emal: string
  artifact_id: number
  subject: string
  issue_kind: string
  target_kind: string
  dominant_kind: string
  kind_shift: boolean
  created_matter: { id: number; client_seq: number; name: string } | null
  target_matter: { id: number; client_seq: number; name: string }
  scenario: string
  thread_total: number
  thread_in_target: number
  confidence: number | null
  identity: number | null
  shared_entities: string[]
  thread_id: string
  mode: string
  status: 'pending' | 'applied' | 'dismissed' | 'auto_applied'
  resolution_note: string
  created_at: string
}

export type ThreadSuggestionsResponse = {
  mode: string
  suggestions: ThreadSuggestion[]
  counts: Record<string, number>
}

export async function listThreadSuggestions(
  statusFilter = 'pending',
): Promise<ThreadSuggestionsResponse> {
  const r = await api.get('/qa/thread-suggestions/', { params: { status: statusFilter } })
  return r.data
}

/** Cheap poll for the QA nav pip: newest thread-issue check timestamp + pending count. */
export async function threadChecksLatest(): Promise<{ latest_at: string | null; count: number }> {
  const r = await api.get<{ latest_at: string | null; count: number }>('/qa/thread-suggestions/latest/')
  return r.data
}

export type ThreadSuggestionApplyResult = {
  ok: boolean
  noop?: boolean
  moved_emails?: number
  archived_matter_ids?: number[]
  siblings_resolved?: number
}
export async function applyThreadSuggestion(id: number): Promise<ThreadSuggestionApplyResult> {
  await ensureCsrf()
  const r = await api.post(`/qa/thread-suggestions/${id}/apply/`, {})
  return r.data
}

export async function dismissThreadSuggestion(
  id: number, reason = '',
): Promise<{ ok: boolean }> {
  await ensureCsrf()
  const r = await api.post(`/qa/thread-suggestions/${id}/dismiss/`, { reason })
  return r.data
}

// De-identified thread-issue queue for the Fireworks cordon (Ken 2026-10-18):
// ids/scores only — no subject, matter names, or shared entities — plus a
// one-click `bundle` (the task manifest) to paste into a Fireworks slot.
export type DeidThreadCheck = {
  check_id: number
  emal: string
  current_matter: string | null
  target_matter: string
  type: string
  thread_total: number
  thread_in_target: number
  confidence: number | null
  identity: number | null
  status: string
  created_at: string
}
export type DeidThreadResponse = {
  mode: string
  deidentified: boolean
  suggestions: DeidThreadCheck[]
  bundle: string
  counts: Record<string, number>
}
export async function listThreadSuggestionsDeid(
  statusFilter = 'pending',
): Promise<DeidThreadResponse> {
  const r = await api.get('/qa/thread-suggestions/', {
    params: { status: statusFilter, deidentified: 1 },
  })
  return r.data
}

// ── Content-free followup corral (A′) monitor — held + suppressed outcomes ────
export type FollowupCorralRow = {
  artifact_id: number
  emal: string
  subject: string
  from_email: string
  kind: string          // read_receipt | auto_reply | bounce
  action: string        // hold | suppress
  thread_id: string
  thread_matters: { client_seq: number; name: string }[]
  source_date: string | null
}
export type FollowupCorralResponse = {
  rows: FollowupCorralRow[]
  counts: { hold: number; suppress: number }
  total: number
}
export async function listFollowupCorral(action = 'all'): Promise<FollowupCorralResponse> {
  const r = await api.get('/qa/followup-corral/', { params: { action } })
  return r.data
}


// ─── Stats (Emerson 2026-08-05) — Workspace Stats page ────────────────
// Two tenant-scoped aggregation endpoints. See apps/pipeline/stats_views.py.
// Continuous window (Ken 2026-08-23): any day count from the Stats dial.
export type StatsWindow = number

export interface StatsTrendPoint {
  bucket: string          // ISO date (bucket start)
  library_size: number    // total matters as of bucket end (2nd x-axis)
  value: number | null    // the rate; null = not measured yet / no denominator
  numerator: number
  denominator: number
}
export interface ProductPerformance {
  window_days: number
  business_days: number   // working-day denominator (Mon–Fri) for the per-day tiles
  corpus_edge_days: number   // days since the earliest email — the window dial's max
  generated_at: string
  measurement_since: string | null
  summary: {
    total_matters: number
    corpus_start: string | null
    matters_opened_in_window: number
    new_matters_per_day: number
    documents_per_day: number
    documents_in_window: number
    emails_arrived: number
    emails_arrived_per_day: number
    emails_filed: number
    emails_filed_per_day: number
    filed_share: number | null
    issues_filed: number
    issues_filed_per_day: number
    refiles_in_window: number
    filings_in_window: number
    refile_rate_overall: number | null
    fragmentations_in_window: number
    fragmentation_rate_overall: number | null
  }
  refile_rate: StatsTrendPoint[]
  fragmentation_rate: StatsTrendPoint[]
}
export interface KindParetoRow { kind: string; count: number }
export interface MatterParetoRow {
  matter_id: number | null; name: string; client_seq: number | null; count: number
}
export interface InstitutionalExhaust {
  window_days: number
  generated_at: string
  total_matters: number
  issues_in_window: number
  issue_kind_pareto: KindParetoRow[]
  matter_pareto: MatterParetoRow[]
  growth_pressure: { bucket: string; opened: number }[]
}
export async function statsProductPerformance(window: StatsWindow): Promise<ProductPerformance> {
  const r = await api.get<ProductPerformance>('/stats/product-performance/', { params: { window } })
  return r.data
}
export async function statsInstitutionalExhaust(window: StatsWindow): Promise<InstitutionalExhaust> {
  const r = await api.get<InstitutionalExhaust>('/stats/institutional-exhaust/', { params: { window } })
  return r.data
}


// Work Product (Heather 2026-08-05) — attorney-flagged emails worth
// keeping. See apps/artifacts WorkProductViewSet.
// Multi-select pull "type" tags (Ken 2026-08-11). Slugs; labels live in the
// Pull modal. Backend whitelists these (WORK_PRODUCT_TYPES).
export type WorkProductType = 'advice' | 'language'
export interface WorkProductExcerpt {
  exact: string
  prefix?: string
  suffix?: string
  note?: string
  types?: WorkProductType[]
  // Markup-only: where in the document this redline/comment sits (derived
  // paragraph/section location, or the quoted anchor). Absent for emails.
  where?: string
  // True for a whole-email pull entry (no selected passage).
  whole_email?: boolean
  created_at?: string
}
export interface WorkProductItem {
  id: number
  artifact: number
  composite_id: string
  artifact_subject: string
  artifact_from: string
  artifact_source_date: string | null
  note: string
  excerpts: WorkProductExcerpt[]
  created_by: number | null
  created_by_name: string
  created_at: string
  matter: number | null
  matter_composite_id: string | null
  matter_name: string | null
  kind_founding: string | null   // (a) first email in the matter
  kind_issue: string | null       // (b) this email's issue
  issue_summary: string
}
export const workProduct = {
  list: (mine = true): Promise<WorkProductItem[]> =>
    api.get('/work-product/', { params: mine ? { mine: 1 } : {} })
      .then(r => (Array.isArray(r.data) ? r.data : r.data.results ?? [])),
  mark: async (
    artifact: number,
    opts: { note?: string; excerpt?: WorkProductExcerpt } = {},
  ): Promise<WorkProductItem> => {
    await ensureCsrf()
    return api.post('/work-product/', {
      artifact, note: opts.note ?? '', excerpt: opts.excerpt,
    }).then(r => r.data)
  },
  unmark: async (id: number): Promise<void> => {
    await ensureCsrf()
    await api.delete(`/work-product/${id}/`)
  },
  // Remove ONE pull (excerpt) from a flag by index. If it was the flag's last
  // excerpt (and no legacy note), the backend deletes the whole flag.
  removeExcerpt: async (id: number, index: number): Promise<void> => {
    await ensureCsrf()
    await api.post(`/work-product/${id}/remove-excerpt/`, { index })
  },
}


export const preseedMatters = {
  list: async (): Promise<{ preseeds: PreseedMatter[] }> => {
    const r = await api.get('/preseed-matters/')
    return r.data
  },
  create: async (payload: PreseedMatterInput): Promise<{ matter_id: number; client_seq: number }> => {
    await ensureCsrf()
    const r = await api.post('/preseed-matters/', payload)
    return r.data
  },
  update: async (matterId: number, payload: PreseedMatterInput): Promise<{ matter_id: number; ok: true }> => {
    await ensureCsrf()
    const r = await api.patch(`/preseed-matters/${matterId}/`, payload)
    return r.data
  },
  remove: async (matterId: number): Promise<void> => {
    await ensureCsrf()
    await api.delete(`/preseed-matters/${matterId}/`)
  },
}


// ─── Studio Claude tab (apps.claude_chat) ─────────────────────────────
export type ClaudeChat = {
  id: number
  title: string
  session_uuid: string
  first_message_preview: string
  last_activity_at: string
  created_at: string
  engine: 'claude' | 'fireworks'
  model: string
}

export type FireworksChatModel = { id: string; label: string; reasoning: boolean; max_tokens: number }

export type ClaudeChatMessage = {
  role: 'user' | 'assistant'
  text: string
  tool_calls?: Array<{ name: string; input_preview: string }>
  ts?: string | null
  // Assistant-only: seconds between the user's prompt and the moment
  // Claude finished streaming the response. Undefined on user rows.
  duration_seconds?: number | null
}

export type ClaudeChatSettings = {
  boilerplate_text: string
  updated_at: string
  fireworks_models?: FireworksChatModel[]
}

export const claudeChats = {
  list: async (): Promise<ClaudeChat[]> => {
    const r = await api.get<{ results: ClaudeChat[] } | ClaudeChat[]>('/claude-chats/')
    if (Array.isArray(r.data)) return r.data
    return (r.data as { results: ClaudeChat[] }).results
  },
  create: async (opts?: { engine?: string; model?: string }): Promise<ClaudeChat> => {
    await ensureCsrf()
    const r = await api.post<ClaudeChat>('/claude-chats/', opts || {})
    return r.data
  },
  updateTitle: async (id: number, title: string): Promise<ClaudeChat> => {
    await ensureCsrf()
    const r = await api.patch<ClaudeChat>(`/claude-chats/${id}/`, { title })
    return r.data
  },
  update: async (id: number, payload: { title?: string; model?: string }): Promise<ClaudeChat> => {
    await ensureCsrf()
    const r = await api.patch<ClaudeChat>(`/claude-chats/${id}/`, payload)
    return r.data
  },
  remove: async (id: number): Promise<void> => {
    await ensureCsrf()
    await api.delete(`/claude-chats/${id}/`)
  },
  messages: async (id: number): Promise<{
    session_uuid: string
    title: string
    engine?: 'claude' | 'fireworks'
    model?: string
    messages: ClaudeChatMessage[]
    is_active: boolean
    seconds_since_last_write: number | null
  }> => {
    const r = await api.get(`/claude-chats/${id}/messages/`)
    return r.data
  },
  // send() uses raw fetch for SSE — see ClaudeTab.tsx.
}

export const claudeChatSettings = {
  get: async (): Promise<ClaudeChatSettings> => {
    const r = await api.get<ClaudeChatSettings>('/claude-chat-settings/')
    return r.data
  },
  update: async (payload: Partial<ClaudeChatSettings>): Promise<ClaudeChatSettings> => {
    await ensureCsrf()
    const r = await api.patch<ClaudeChatSettings>('/claude-chat-settings/1/', payload)
    return r.data
  },
}

// ── Contract VP map (CIC) ───────────────────────────────────────────────
export type VpMapSummary = {
  client: string; count: number; self: number; placeholder: number
  unresolved: Array<{ submitter: string; vp_name: string }>
}
export async function vpMapGet(code: string): Promise<VpMapSummary> {
  const r = await api.get<VpMapSummary>('/qa/cic-vp-map/', { params: { client: code } })
  return r.data
}
export async function vpMapIngest(code: string, csv: string): Promise<{ ok: boolean; report: any; summary: VpMapSummary }> {
  await ensureCsrf()
  const r = await api.post('/qa/cic-vp-map/', { client: code, csv })
  return r.data
}

// ─── Secretary chat (Ken 2026-10-22, KEN/playhead only) ───────────────────
// A container-scoped chat over the tenant's correspondence. Backend assembles
// the scoped corpus and calls Fireworks gpt-oss-120b. KEN-gated server-side.
export type SecretaryScope = { type: 'matter' | 'folder' | 'unfiled'; id?: number }
export type SecretarySource = {
  code: string; artifact_id: number; matter_id: number
  // Citation-form fields (Ken 2026-10-24): sender first name, subject, MM/DD/YY date.
  sender?: string; subject?: string; date?: string
}
export async function secretaryChat(
  scope: SecretaryScope,
  messages: { role: 'user' | 'assistant'; content: string }[],
): Promise<{ reply: string; matters: number; sources: SecretarySource[] }> {
  await ensureCsrf()
  const r = await api.post('/secretary-chat/', { scope, messages })
  return r.data
}
